import { useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertCircle,
  Check,
  Loader2,
  Search,
  UsersRound,
  X,
} from "lucide-react";

import { createChatGroupApi } from "../../app/api";

export default function CreateGroupModal({ employees, onClose, onGroupCreated }) {
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const filteredEmployees = employees
    .filter((employee) => {
      const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`;
      return fullName.toLowerCase().includes(memberSearch.trim().toLowerCase());
    })
    .sort((firstEmployee, secondEmployee) => {
      const firstName = `${firstEmployee.first_name || ""} ${firstEmployee.last_name || ""}`;
      const secondName = `${secondEmployee.first_name || ""} ${secondEmployee.last_name || ""}`;
      return firstName.localeCompare(secondName, undefined, { sensitivity: "base" });
    });

  const toggleMember = (id) => {
    setError("");
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((memberId) => memberId !== id) : [...prev, id]
    );
  };

  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError("Please enter a group name before creating the group.");
      return;
    }

    if (selectedMembers.length === 0) {
      setError("Select at least one teammate to add to this group.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await createChatGroupApi({
        name: groupName.trim(),
        members: selectedMembers,
      });

      if (res?.success) {
        onGroupCreated();
        onClose();
        return;
      }

      setError(res?.message || "Failed to create group. Please try again.");
    } catch (err) {
      console.error("Create group error:", err);
      setError("Failed to create group. Please confirm you have admin access.");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-md">
      <div className="relative w-full max-w-[430px] overflow-hidden rounded-3xl border border-white/10 bg-[#161320] text-white shadow-[0_30px_100px_rgba(0,0,0,0.55)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.38),transparent_38%),radial-gradient(circle_at_top_right,rgba(14,165,233,0.18),transparent_36%)]" />

        <div className="relative border-b border-white/10 px-5 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-500/20 text-violet-100 ring-1 ring-white/15">
                <UsersRound size={23} />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Create Group</h3>
                <p className="mt-1 text-xs text-white/45">
                  Build a quick team conversation.
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-white/70 transition hover:bg-white/20 hover:text-white"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="relative space-y-4 p-5">
          {error && (
            <div className="flex gap-3 rounded-2xl border border-rose-300/20 bg-rose-500/12 px-4 py-3 text-sm text-rose-100">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-white/45">
              Group Name
            </label>
            <input
              className="w-full rounded-2xl border border-white/10 bg-white/[0.07] px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-violet-300/60 focus:bg-white/[0.1] focus:ring-4 focus:ring-violet-500/10"
              placeholder="Example: Design Team"
              value={groupName}
              onChange={(event) => {
                setGroupName(event.target.value);
                setError("");
              }}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium uppercase tracking-wide text-white/45">
                Members
              </label>
              <span className="rounded-full bg-violet-500/15 px-2.5 py-1 text-[11px] font-semibold text-violet-100">
                {selectedMembers.length} selected
              </span>
            </div>

            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-white/35" />
              <input
                value={memberSearch}
                onChange={(event) => setMemberSearch(event.target.value)}
                placeholder="Search teammates..."
                className="w-full rounded-2xl border border-white/10 bg-white/[0.05] py-2.5 pl-10 pr-3 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-violet-300/50 focus:bg-white/[0.08]"
              />
            </div>

            <div className="custom-scrollbar max-h-[300px] overflow-y-auto rounded-2xl border border-white/10 bg-black/10 p-2">
              {filteredEmployees.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm font-medium text-white/70">No teammates found</p>
                  <p className="mt-1 text-xs text-white/35">Try another name.</p>
                </div>
              ) : (
                filteredEmployees.map((employee) => {
                  const isSelected = selectedMembers.includes(employee.id);

                  return (
                    <button
                      key={employee.id}
                      type="button"
                      className={`mb-1 flex w-full items-center gap-3 rounded-2xl p-2.5 text-left transition last:mb-0 ${
                        isSelected
                          ? "bg-violet-500/15 ring-1 ring-violet-300/25"
                          : "hover:bg-white/[0.07]"
                      }`}
                      onClick={() => toggleMember(employee.id)}
                    >
                      <span
                        className={`flex h-10 w-10 items-center justify-center rounded-2xl text-sm font-bold ${
                          isSelected
                            ? "bg-violet-500 text-white"
                            : "bg-white/10 text-white/80"
                        }`}
                      >
                        {employee.first_name?.charAt(0)}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-white">
                          {employee.first_name} {employee.last_name}
                        </span>
                        <span className="text-xs text-white/35">Teammate</span>
                      </span>

                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-md border transition ${
                          isSelected
                            ? "border-violet-300 bg-violet-500 text-white"
                            : "border-white/20 bg-white/5"
                        }`}
                      >
                        {isSelected && <Check size={13} strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={onClose}
              className="rounded-xl border border-white/10 bg-white/10 px-4 py-2.5 text-sm text-white/70 transition hover:bg-white/15 hover:text-white"
            >
              Cancel
            </button>

            <button
              onClick={handleCreate}
              disabled={loading}
              className="flex min-w-[96px] items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-950/30 transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Creating" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
